import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const StorySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.3 });

  const storyMilestones = [
    {
      year: "1995",
      title: "The Vision Begins",
      description: `Sri Dhanvantari Ayurvaidik Parishkar Kendra was established with a commitment to bring traditional Ayurveda into structured, modern therapeutic care. \nThe inspiration and blessings of Swami Shri Swaroopanand Saraswati Ji Maharaj—of the Dwarka Sharada Peetham and Jyotir Math—quietly shaped the ethos and name of the centre, grounding it in a lineage of timeless wisdom.`,
      icon: "Lightbulb",
      image: "/assets/images/reception.png?w=600&h=400&fit=crop"
    },
    {
      year: "2010",
      title: "Foundation of Healing",
      description: `Established as an Authorized Kottakkal Agency,\nwith a commitment to bring classical Ayurvedic wisdom into structured, protocol-driven therapeutic practice.\n\nBegan delivering root-cause–based Ayurvedic care for chronic and lifestyle disorders, grounded in authentic formulations and time-tested principles.`,
      icon: "Leaf",
      image: "/assets/images/medicines.png?w=600&h=400&fit=crop"
    },
    {
      year: "2014",
      title: "Psoriasis & Skin Disorders Expertise",
      description: `Developed specialized Ayurveda protocols for Psoriasis, Eczema, and Chronic Dermatitis.\nAchieved consistent long-term outcomes using Panchakarma-backed healing methods.`,
      icon: "Sun",
      image: "https://images.unsplash.com/photo-1606884544471-3cf5a3cffb0e?w=600&h=400&fit=crop"
    },
    {
      year: "2018",
      title: "Autoimmune Care Breakthrough",
      description: `Introduced structured treatment workflows for autoimmune conditions like Rheumatoid Arthritis, Ulcerative Colitis, Hashimoto’s, and Vitiligo.\nIntegrated diet, gut-healing, and personalized herbal therapies.`,
      icon: "Sparkles",
      image: "https://images.unsplash.com/photo-1607614204367-48e8d8530f2b?w=600&h=400&fit=crop"
    },
    {
      year: "2023",
      title: "Technology + Ayurveda Integration",
      description: `Digitized patient records, treatment workflows, and follow-up protocols.\nEnabled modern reporting, progress tracking, and structured long-term care.`,
      icon: "Cpu",
      image: "/assets/images/treatment-room.jpeg?w=600&h=400&fit=crop"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="font-headline text-3xl md:text-5xl font-bold text-foreground mb-6">
            Our <span className="text-morph">Journey</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-accent">
            From a small center to a leading Ayurvedic institution, Sri Dhanvantari Ayurvaidik Parishkar Kendra has redefined holistic healthcare through authentic therapies, evidence-based protocols, and a commitment to patient well-being.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-accent to-primary opacity-30 hidden lg:block"></div>

          {/* Milestones */}
          <div className="space-y-20">
            {storyMilestones?.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
              >
                {/* Content */}
                <div className="flex-1 lg:max-w-md">
                  <div className="glass-card p-8 morph-hover">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="glass-panel p-3 rounded-full">
                        <Icon name={milestone?.icon} size={24} className="text-primary" />
                      </div>
                      <div className="text-2xl font-bold text-primary font-headline">
                        {milestone?.year}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-foreground mb-4 font-headline">
                      {milestone?.title}
                    </h3>

                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {milestone?.description}
                    </p>
                  </div>
                </div>

                {/* Timeline Node */}
                <div className="hidden lg:block relative z-10">
                  <div className="w-4 h-4 bg-primary rounded-full border-4 border-background shadow-lg"></div>
                </div>

                {/* Image */}
                <div className="flex-1 lg:max-w-md">
                  <motion.div
                    className="glass-card p-4 morph-hover"
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="overflow-hidden rounded-lg">
                      <Image
                        src={milestone?.image}
                        alt={milestone?.title}
                        className="w-full h-64 object-cover transition-transform duration-500 hover:scale-110"
                      />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Philosophy Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-32 text-center"
        >
          <div className="glass-card p-12 max-w-4xl mx-auto">
            <Icon name="Quote" size={48} className="text-primary mx-auto mb-8 opacity-50" />
            <blockquote className="text-xl md:text-2xl text-foreground font-accent italic leading-relaxed mb-8">
              "Healing is not about changing who you are—it is about restoring the balance and strength already present within you.
              At Sri Dhanvantari Ayurvaidik Parishkar Kendra, our mission is to create a space where authentic Ayurveda helps you rediscover your natural state of health and harmony."
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <Image
                  src="/assets/images/haresh.jpeg"
                  alt="Founder"
                  className="w-full h-full object-roundedfull"
                />
              </div>

              <div className="text-left">
                <div className="font-semibold text-foreground">Dr. L. Haresh</div>
                <div className="text-sm text-muted-foreground">Founder & Chief Physician</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StorySection;